import { Component, Input, Inject } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { Error } from '@app/core/models/Error';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { KeyValuePair } from '@app/core/models/keyValuePair';
import { Actives } from '@app/core/constants';
import { HeadWorkingType } from '@app/core/enums/head-working-type.enum';
import { EditHeadworkingDialogData } from '@app/core/models/edit-headworking-dialog/edit-headworking-dialog-data';
import { HeadworkingValues } from '@app/core/models/edit-headworking-dialog/headworking-values';
import { EditHeadworkingDialogResult } from '@app/core/models/edit-headworking-dialog/edit-headworking-dialog-result';

@Component({
  templateUrl: './edit-headworking-dialog.component.html',
  styleUrls: ['./edit-headworking-dialog.component.scss'],
})
export class EditHeadworkingDialogComponent {
  @Input()
  set pending(isPending: boolean) {
    if (isPending) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Input() errorModel: Error[] | null;

  isNew: boolean;
  systemDepth: number;
  headWorkingTypes: KeyValuePair[];
  standard = true;
  showSecondHeadWorking = false;

  actives = Actives.keyValuePairs;

  form: FormGroup = new FormGroup({
    headWorkingType: new FormControl(0, { validators: [Validators.required], updateOn: 'blur' }),
    multipleHeadWorkings: new FormControl(false, { updateOn: 'blur' }),
    headWorkingOne: new FormControl(0, { validators: [Validators.required, Validators.min(0)], updateOn: 'blur' }),
    headWorkingTwo: new FormControl(0, { validators: [Validators.required, Validators.min(0)], updateOn: 'blur' }),
    headWorkingFour: new FormControl(0, { validators: [Validators.required, Validators.min(0)], updateOn: 'blur' }),
    secondHeadWorkingOne: new FormControl(0, { updateOn: 'blur' }),
    secondHeadWorkingTwo: new FormControl(0, { updateOn: 'blur' }),
    secondHeadWorkingFour: new FormControl(0, { updateOn: 'blur' }),
    miterDegrees: new FormControl(0, { validators: [Validators.min(1), Validators.max(89)], updateOn: 'blur' }),
  });

  constructor(
    private readonly matDialogRef: MatDialogRef<EditHeadworkingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: EditHeadworkingDialogData
  ) {
    this.isNew = data.isNew;
    this.systemDepth = data.systemDepth;
    this.headWorkingTypes = data.headWorkingTypes;

    if (data.selectedHeadWorkingType !== null) {
      this.form.patchValue({ headWorkingType: data.selectedHeadWorkingType });
      this.changeHeadWorkingType(data.selectedHeadWorkingType);
      
      if (data.headWorkingValues.length > 0) {
        const firstHeadworking = data.headWorkingValues[0];

        if (this.standard) {
          this.form.patchValue({ headWorkingOne: firstHeadworking.headWorkingOne, headWorkingTwo: firstHeadworking.headWorkingTwo, headWorkingFour: firstHeadworking.headWorkingFour });

          if (data.headWorkingValues.length > 1) {
            this.changeMultipleHeadWorkings(true);
            const secondHeadworking = data.headWorkingValues[1];
            this.form.patchValue({ multipleHeadWorkings: true, secondHeadWorkingOne: secondHeadworking.headWorkingOne, secondHeadWorkingTwo: secondHeadworking.headWorkingTwo, secondHeadWorkingFour: secondHeadworking.headWorkingFour });
          }
        } else {
          this.form.patchValue({ miterDegrees: firstHeadworking.miterDegrees });
        }
      }
    } else {
        this.changeHeadWorkingType(HeadWorkingType.Standard);
    }
  }

  saveHeadworking() {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.value;

    let headWorkingValues: HeadworkingValues[] = [];
    
    if (this.standard) {
      headWorkingValues.push({ 
        headWorkingOne: formValue.headWorkingOne,
        headWorkingTwo: formValue.headWorkingTwo,
        headWorkingFour: formValue.headWorkingFour,
        miterDegrees: 0
      });

      if (formValue.multipleHeadWorkings) {
        headWorkingValues.push({ 
          headWorkingOne: formValue.secondHeadWorkingOne, 
          headWorkingTwo: formValue.secondHeadWorkingTwo, 
          headWorkingFour: formValue.secondHeadWorkingFour,
          miterDegrees: 0
        });
      }
    } else {
      headWorkingValues.push({ 
        headWorkingOne: 0, 
        headWorkingTwo: 0, 
        headWorkingFour: 0,
        miterDegrees: formValue.miterDegrees
      });
    }

    const returnValue: EditHeadworkingDialogResult = {
      headWorkingType: formValue.headWorkingType,
      headWorkingValues
    }

    this.matDialogRef.close(returnValue);
  }

  cancel() {
    this.matDialogRef.close(null);
  }

  changeHeadWorkingType(newType: HeadWorkingType | null) {
    if (newType === null) {
      return;
    }
    
    let headWorkingValidators: ValidatorFn[] = [];
    let miterValidators: ValidatorFn[] = [];

    if (newType === HeadWorkingType.Standard) {
      this.standard = true;
      headWorkingValidators = [Validators.required, Validators.min(0)];
      miterValidators = [];
      this.form.patchValue({ miterDegrees: null });
    } else {
      this.standard = false;
      miterValidators = [Validators.required, Validators.min(1), Validators.max(89)];
      headWorkingValidators = [];
      this.form.patchValue({ headWorkingOne: null, headWorkingTwo: null, headWorkingFour: null, secondHeadWorkingOne: null, secondHeadWorkingTwo: null, secondHeadWorkingFour: null });
    }

    this.form.patchValue({ multipleHeadWorkings: false });
    this.showSecondHeadWorking = false;
    this.setValidators(headWorkingValidators, [], miterValidators);
  }
  
  changeMultipleHeadWorkings(value: boolean) {
    const secondHeadWorkingValidators: ValidatorFn[] = value ? [Validators.required, Validators.min(0)] : [];
    
    const headWorkingValue = value ? 0 : null;
    this.form.patchValue({ secondHeadWorkingOne: headWorkingValue, secondHeadWorkingTwo: headWorkingValue, secondHeadWorkingFour: headWorkingValue });
    
    this.setValidators([Validators.required, Validators.min(0)], secondHeadWorkingValidators, []);
    
    this.showSecondHeadWorking = value;
  }

  setValidators(headWorkingValidators: ValidatorFn[], secondHeadWorkingValidators: ValidatorFn[], miterValidators: ValidatorFn[]) {
    this.form.get('headWorkingOne').setValidators(headWorkingValidators);
    this.form.get('headWorkingTwo').setValidators(headWorkingValidators);
    this.form.get('headWorkingFour').setValidators(headWorkingValidators);
    
    this.form.get('secondHeadWorkingOne').setValidators(secondHeadWorkingValidators);
    this.form.get('secondHeadWorkingTwo').setValidators(secondHeadWorkingValidators);
    this.form.get('secondHeadWorkingFour').setValidators(secondHeadWorkingValidators);

    this.form.get('miterDegrees').setValidators(miterValidators);
    
    this.form.get('headWorkingOne').updateValueAndValidity();
    this.form.get('headWorkingTwo').updateValueAndValidity();
    this.form.get('headWorkingFour').updateValueAndValidity();
    
    this.form.get('secondHeadWorkingOne').updateValueAndValidity();
    this.form.get('secondHeadWorkingTwo').updateValueAndValidity();
    this.form.get('secondHeadWorkingFour').updateValueAndValidity();

    this.form.get('miterDegrees').updateValueAndValidity();
  }

  get errors(): Error[] {
    return this.errorModel ? this.errorModel : [];
  }
}
